import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, ArrowRight, ShieldCheck, AlertTriangle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { authApi } from "@/lib/api/auth";
import { isSuccess } from "@/lib/api/types";
import { useAuthStore } from "@/lib/auth/store";

type Step = "email" | "otp";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setTokens = useAuthStore((s) => s.setTokens);
  const setSession = useAuthStore((s) => s.setSession);

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reason = params.get("reason");
  const from = params.get("from") ?? "/";

  async function requestCode() {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await authApi.requestOtp({ identifier: email });
      if (isSuccess(data)) setStep("otp");
      else if (data.code === "AUTH-12") setError("Too many attempts. Try again in a minute.");
      else setError(`We couldn't send a code (${data.code}).`);
    } catch {
      setError("Network error. Check your connection.");
    } finally {
      setSubmitting(false);
    }
  }

  async function verify() {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await authApi.verifyOtp({ identifier: email, code });
      // verifyOtp success returns AuthTokens (code AUTH-01). Key off the
      // tokens payload, not the code suffix — success codes vary per module.
      if (data.data?.accessToken) {
        const tokens = data.data;
        if (tokens.role !== "ADMIN") {
          setError("This account isn't an admin. Access denied.");
          return;
        }
        setTokens(tokens.accessToken, tokens.refreshToken);
        setSession({
          userId: tokens.userId,
          email: tokens.email ?? email,
          name: tokens.name ?? email.split("@")[0],
          role: "ADMIN",
          photoUrl: tokens.photoUrl,
        });
        navigate(from, { replace: true });
      } else if (data.code === "AUTH-11") {
        setError("That code is invalid or expired.");
      } else {
        setError(`Sign-in failed (${data.code}).`);
      }
    } catch {
      setError("Network error. Check your connection.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background overflow-hidden">
      {/* Decorative sunset orb */}
      <div className="pointer-events-none absolute -top-32 -right-32 size-[420px] rounded-full bg-gradient-sunset opacity-30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 size-[420px] rounded-full bg-gradient-sunset opacity-20 blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="relative w-full max-w-md px-6"
      >
        <div className="mb-6 flex flex-col items-center gap-4">
          <img
            src="/logos/afya24-wordmark.png"
            alt="Afya24"
            className="h-16 w-auto"
          />
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
              Admin Portal
            </span>
            <p className="text-sm text-muted-foreground mt-3">
              Sign in to manage the platform.
            </p>
          </div>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle>{step === "email" ? "Welcome back" : "Enter verification code"}</CardTitle>
            <CardDescription>
              {step === "email"
                ? "We'll email you a 6-digit code."
                : `Sent to ${email}. Codes expire in 5 minutes.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {reason === "expired" && step === "email" && (
              <div className="flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
                <AlertTriangle className="size-3.5" /> Your session expired — please sign in again.
              </div>
            )}
            {reason === "idle" && step === "email" && (
              <div className="flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
                <AlertTriangle className="size-3.5" /> Signed out after 15 minutes of inactivity.
              </div>
            )}

            {step === "email" ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  requestCode();
                }}
                className="space-y-3"
              >
                <label className="text-sm font-semibold">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    type="email"
                    autoFocus
                    required
                    placeholder="you@afya24.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                  />
                </div>
                {error && <p className="text-xs text-destructive">{error}</p>}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={submitting || !email.includes("@")}
                >
                  {submitting ? "Sending…" : "Send verification code"}
                  <ArrowRight className="size-4" />
                </Button>
              </form>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  verify();
                }}
                className="space-y-3"
              >
                <label className="text-sm font-semibold">6-digit code</label>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="h-12 text-center text-2xl font-bold tracking-[0.4em]"
                  placeholder="••••••"
                />
                {error && <p className="text-xs text-destructive">{error}</p>}
                <Button type="submit" size="lg" className="w-full" disabled={submitting || code.length !== 6}>
                  {submitting ? "Verifying…" : "Verify and sign in"}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setCode("");
                    setError(null);
                  }}
                  className="cursor-pointer text-xs text-muted-foreground hover:text-foreground"
                >
                  ← Use a different email
                </button>
              </form>
            )}

            <div className="flex items-center gap-2 pt-2 text-[11px] text-muted-foreground">
              <ShieldCheck className="size-3.5 text-protected" />
              All admin actions are audited. Idle sessions sign out after 15 minutes.
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
